import type { HealthState, PartnerOverview as OverviewData } from '@central/api';
import {
  Card,
  HealthChip,
  KpiTile,
  PageHeader,
  StatusChip,
  color,
  formatAgo,
  formatDate,
  status,
  toneForHealth,
} from '@central/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { CSSProperties, ReactNode } from 'react';
import { keys, useApi } from '../../api';
import { Empty, QueryState } from '../../components/parts';
import { muted } from '../../lib/labels';
import { statusLabel } from '../../lib/reports';

/** How often the overview refreshes. */
const refreshEvery = 30_000;

/** The byHealth keys in display order; updating counts as up. */
const healthOrder = ['up', 'degraded', 'down', 'unknown'] as const satisfies readonly HealthState[];

const list: CSSProperties = { listStyle: 'none', margin: 0, padding: 0 };

const row: CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  padding: '12px 20px',
  borderTop: `1px solid ${color.rowRule}`,
  fontSize: 14,
};

/** A reseller's home: its customers, their installations and support at a glance. */
export function PartnerOverview() {
  const api = useApi();
  const overview = useQuery({
    queryKey: keys.partnerOverview,
    queryFn: () => api.partner.overview(),
    refetchInterval: refreshEvery,
  });
  return (
    <>
      <PageHeader
        title="Overview"
        subtitle="Your customers, their installations and their support tickets"
      />
      <QueryState isPending={overview.isPending} error={overview.error}>
        {() => <OverviewBody data={overview.data!} />}
      </QueryState>
    </>
  );
}

function OverviewBody({ data }: { data: OverviewData }) {
  const t = data.tickets;
  return (
    <>
      <ul
        aria-label="Totals"
        style={{
          ...list,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        <li>
          <KpiTile label="Customers" value={data.customers.total} />
        </li>
        <li>
          <KpiTile label="Environments" value={data.environments.total} />
        </li>
        <li>
          <KpiTile label="Open tickets" value={t.open} />
        </li>
        <li>
          <KpiTile
            label="Awaiting your reply"
            value={t.awaitingReseller}
            valueColor={t.awaitingReseller > 0 ? status.warn.fg : undefined}
          />
        </li>
        <li>
          <KpiTile label="Unread" value={t.unread} />
        </li>
        <li>
          <KpiTile label="Escalated to Rethink" value={t.escalated} />
        </li>
      </ul>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <HealthCard data={data} />
        <AttentionCard data={data} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <TicketsCard data={data} />
        <ReleaseCard data={data} />
      </div>
    </>
  );
}

function HealthCard({ data }: { data: OverviewData }) {
  const e = data.environments;
  const bar = (n: number, fill: string) => (
    <div
      aria-hidden
      style={{ flex: 1, height: 8, borderRadius: 4, background: color.rowRule, minWidth: 40 }}
    >
      <div
        style={{
          width: e.total > 0 ? `${(n / e.total) * 100}%` : 0,
          height: '100%',
          borderRadius: 4,
          background: fill,
        }}
      />
    </div>
  );
  const line = (key: string, chip: ReactNode, n: number, fill: string) => (
    <li key={key} style={row}>
      <span style={{ width: 120 }}>{chip}</span>
      {bar(n, fill)}
      <b style={{ width: 32, textAlign: 'right' }}>{n}</b>
    </li>
  );
  return (
    <Card
      title="Environment health"
      padded={false}
      style={{ flex: '2 1 320px' }}
      action={
        <Link to="/partner/environments" search={{}} style={{ fontSize: 13 }}>
          All environments
        </Link>
      }
    >
      <ul aria-label="Environment health" style={list}>
        {healthOrder.map((h) =>
          line(h, <HealthChip state={h} />, e.byHealth[h], status[toneForHealth(h)].fg),
        )}
        {line(
          'not_enrolled',
          <StatusChip tone="unk">Not enrolled</StatusChip>,
          e.notEnrolled,
          status.unk.fg,
        )}
      </ul>
    </Card>
  );
}

function AttentionCard({ data }: { data: OverviewData }) {
  const items = data.environmentsNeedingAttention;
  return (
    <Card title="Needs attention" padded={false} style={{ flex: '3 1 420px' }}>
      {items.length === 0 ? (
        <Empty>No environment needs attention.</Empty>
      ) : (
        <ul aria-label="Environments needing attention" style={list}>
          {items.map((e) => (
            <li key={e.id} style={row}>
              <HealthChip state={e.health} />
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <Link
                  to="/partner/environments/$environmentId"
                  params={{ environmentId: e.id }}
                  style={{ fontWeight: 600 }}
                >
                  {e.name}
                </Link>
                <span style={{ fontSize: 13, ...muted }}>
                  {e.customer.name} ·{' '}
                  {e.lastHeartbeatAt
                    ? `last report ${formatAgo(e.lastHeartbeatAt)}`
                    : 'Never reported'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function TicketsCard({ data }: { data: OverviewData }) {
  return (
    <Card
      title="Recent tickets"
      padded={false}
      style={{ flex: '3 1 420px' }}
      action={
        <Link to="/inbox" search={{ tier: 'tier1' }} style={{ fontSize: 13 }}>
          Support inbox
        </Link>
      }
    >
      {data.recentTickets.length === 0 ? (
        <Empty>No tickets yet.</Empty>
      ) : (
        <ul aria-label="Recent tickets" style={list}>
          {data.recentTickets.map((t) => (
            <li key={t.id} style={row}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <Link to="/inbox/$threadId" params={{ threadId: t.id }} style={{ fontWeight: 600 }}>
                  {t.subject}
                </Link>
                <span style={{ fontSize: 13, ...muted }}>
                  {t.ref} · {t.customer.name} · {formatAgo(t.lastMessageAt)}
                </span>
              </div>
              <span style={{ fontSize: 13, ...muted }}>{statusLabel[t.status]}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ReleaseCard({ data }: { data: OverviewData }) {
  const r = data.latestRelease;
  return (
    <Card
      title="Latest release"
      style={{ flex: '2 1 320px' }}
      action={
        <Link to="/partner/releases" style={{ fontSize: 13 }}>
          All releases
        </Link>
      }
    >
      {r ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <b style={{ fontSize: 22 }}>{r.version}</b>
          <span style={{ fontSize: 13, ...muted }}>Published {formatDate(r.publishedAt)}</span>
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 14, ...muted }}>No release published yet.</p>
      )}
    </Card>
  );
}
